import { brands, BrandType } from 'noddi-types';

// Import all brand images in different formats,
// AVIF, WebP, and JPG for compatibility and performance.
import bestdriveAvif from '../../assets/images/bestdrive/brand-image/bestdrive.avif';
import bestdriveJpg from '../../assets/images/bestdrive/brand-image/bestdrive.jpg';
import bestdriveWebp from '../../assets/images/bestdrive/brand-image/bestdrive.webp';

import carglassAvif from '../../assets/images/carglass/brand-image/carglass.avif';
import carglassJpg from '../../assets/images/carglass/brand-image/carglass.jpg';
import carglassWebp from '../../assets/images/carglass/brand-image/carglass.webp';

import dekkfixAvif from '../../assets/images/dekkfix/brand-image/dekkfix.avif';
import dekkfixJpg from '../../assets/images/dekkfix/brand-image/dekkfix.jpg';
import dekkfixWebp from '../../assets/images/dekkfix/brand-image/dekkfix.webp';

import dekkstraAvif from '../../assets/images/dekkstra/brand-image/dekkstra.avif';
import dekkstraJpg from '../../assets/images/dekkstra/brand-image/dekkstra.jpg';
import dekkstraWebp from '../../assets/images/dekkstra/brand-image/dekkstra.webp';

import noddiAvif from '../../assets/images/noddi/brand-image/noddi.avif';
import noddiJpg from '../../assets/images/noddi/brand-image/noddi.jpg';
import noddiWebp from '../../assets/images/noddi/brand-image/noddi.webp';

import tronderdekkAvif from '../../assets/images/tronderdekk/brand-image/tronderdekk.avif';
import tronderdekkJpg from '../../assets/images/tronderdekk/brand-image/tronderdekk.jpg';
import tronderdekkWebp from '../../assets/images/tronderdekk/brand-image/tronderdekk.webp';

import dekkteamAvif from '../../assets/images/dekkteam/brand-image/dekkteam.avif';
import dekkteamJpg from '../../assets/images/dekkteam/brand-image/dekkteam.png';
import dekkteamWebp from '../../assets/images/dekkteam/brand-image/dekkteam.webp';

import shineAvif from '../../assets/images/shine/brand-image/shine.avif';
import shineJpg from '../../assets/images/shine/brand-image/shine.jpg';
import shineWebp from '../../assets/images/shine/brand-image/shine.webp';

import lotusAvif from '../../assets/images/lotus/brand-image/lotus.avif';
import lotusJpg from '../../assets/images/lotus/brand-image/lotus.png';
import lotusWebp from '../../assets/images/lotus/brand-image/lotus.webp';

import myldreAvif from '../../assets/images/myldre/brand-image/myldre.avif';
import myldreJpg from '../../assets/images/myldre/brand-image/myldre.png';
import myldreWebp from '../../assets/images/myldre/brand-image/myldre.webp';

import meksAvif from '../../assets/images/meks/brand-image/meks.avif';
import meksJpg from '../../assets/images/meks/brand-image/meks.png';
import meksWebp from '../../assets/images/meks/brand-image/meks.webp';

type BrandImageSources = {
  avif: string | undefined;
  webp: string | undefined;
  jpg: string | undefined;
};

const brandImageSources: Record<BrandType, BrandImageSources> = {
  [brands.bestdrive]: {
    avif: bestdriveAvif,
    webp: bestdriveWebp,
    jpg: bestdriveJpg
  },
  [brands.carglass]: {
    avif: carglassAvif,
    webp: carglassWebp,
    jpg: carglassJpg
  },
  [brands.dekkfix]: {
    avif: dekkfixAvif,
    webp: dekkfixWebp,
    jpg: dekkfixJpg
  },
  [brands.dekkstra]: {
    avif: dekkstraAvif,
    webp: dekkstraWebp,
    jpg: dekkstraJpg
  },
  [brands.noddi]: {
    avif: noddiAvif,
    webp: noddiWebp,
    jpg: noddiJpg
  },
  [brands.tronderdekk]: {
    avif: tronderdekkAvif,
    webp: tronderdekkWebp,
    jpg: tronderdekkJpg
  },
  [brands.elitebilvask]: {
    avif: undefined, // Placeholder for future image
    webp: undefined, // Placeholder for future image
    jpg: undefined // Placeholder for future image
  },
  [brands.dekkteam]: {
    avif: dekkteamAvif,
    webp: dekkteamWebp,
    jpg: dekkteamJpg
  },
  [brands.shine]: {
    avif: shineAvif,
    webp: shineWebp,
    jpg: shineJpg
  },
  [brands.lotus]: {
    avif: lotusAvif,
    webp: lotusWebp,
    jpg: lotusJpg
  },
  [brands.myldre]: {
    avif: myldreAvif,
    webp: myldreWebp,
    jpg: myldreJpg
  },
  [brands.meks]: {
    avif: meksAvif,
    webp: meksWebp,
    jpg: meksJpg
  }
};

interface BrandImageProps {
  brand: BrandType;
  alt?: string;
  className?: string;
}

export function BrandImage({ brand, alt, className }: BrandImageProps) {
  const sources = brandImageSources[brand] || brandImageSources[brands.noddi];
  const brandName = brand.charAt(0).toUpperCase() + brand.slice(1);
  const altText = alt || `${brandName} brand image`;

  if (!sources.jpg && !sources.webp && !sources.avif) {
    // If no image is available for the brand, return null or a placeholder
    return null;
  }

  return (
    <picture>
      <source srcSet={sources.avif} type='image/avif' />
      <source srcSet={sources.webp} type='image/webp' />
      <img src={sources.jpg} alt={altText} className={className ?? 'h-auto w-full'} />
    </picture>
  );
}
